import React from "react";
import { PreviewLoadingOverlay } from "../../components/LoadingStates";
import { EmbedSnippet } from "../../components/EmbedSnippet";
import { cn } from "@/lib/utils";
import {
  MOBILE_BACKDROP_CLASSES,
  MOBILE_DEVICE_CLASSES,
  PageLinkActions,
  PreviewFrame,
  PreviewToolbar,
} from "../../components/PreviewPanel";
import { MOBILE_PREVIEW_WIDTH, PreviewPlayerState } from "../../components/usePreviewPlayer";
import { EMBED_IFRAME_HEIGHT } from "./useEmbeddedHighlightsPreview";

interface Props {
  preview: PreviewPlayerState;
  iframeRef: React.RefObject<HTMLIFrameElement>;
  iframeSrc: string;
  includeAutoResize: boolean;
  onIncludeAutoResizeChange: (includeAutoResize: boolean) => void;
  iframeHeight: number;
  isLoading: boolean;
  previewPlayerName?: string;
  iframeSnippet: string;
  embedUrl: string;
  isSamplePreview: boolean;
}

const EmbeddedHighlightsPreview: React.FC<Props> = ({
  preview,
  iframeRef,
  iframeSrc,
  includeAutoResize,
  onIncludeAutoResizeChange,
  iframeHeight,
  isLoading,
  previewPlayerName,
  iframeSnippet,
  embedUrl,
  isSamplePreview,
}) => {
  const isMobilePreview = preview.previewDevice === "mobile";
  const previewHeight = includeAutoResize ? iframeHeight : EMBED_IFRAME_HEIGHT;

  return (
    <div className="flex min-w-0 flex-col gap-3 lg:min-h-0 lg:flex-1">
      <PreviewToolbar preview={preview} />

      <div className="no-scrollbar flex min-w-0 flex-col gap-3 lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
        <PreviewFrame
          address={embedUrl}
          addressActions={<PageLinkActions url={embedUrl} isTemplate={isSamplePreview} />}
          className="shrink-0"
        >
          <div className={cn("relative min-w-0 w-full", isMobilePreview ? MOBILE_BACKDROP_CLASSES : "bg-white")}>
            <iframe
              ref={iframeRef}
              title="Embedded Highlights preview"
              src={iframeSrc}
              className={cn("block border-0 bg-white", isMobilePreview ? ["mx-auto", MOBILE_DEVICE_CLASSES] : "w-full")}
              style={{
                height: previewHeight,
                ...(isMobilePreview ? { width: MOBILE_PREVIEW_WIDTH, maxWidth: "100%" } : undefined),
              }}
            />
            {isLoading && <PreviewLoadingOverlay playerName={previewPlayerName} />}
          </div>
        </PreviewFrame>

        <EmbedSnippet
          snippet={iframeSnippet}
          includeAutoResize={includeAutoResize}
          onIncludeAutoResizeChange={onIncludeAutoResizeChange}
        />
      </div>
    </div>
  );
};

export { EmbeddedHighlightsPreview };
